/**
 * Refrigerant emission factors (Scope 1 — Fugitive)
 * Factor source: UK DEFRA 2024
 * GWP basis: AR5 (100-year), kgCO2e per kg of refrigerant leaked
 *
 * This is the single source of truth used by:
 *  - Frontend: dropdown options in CarbonEmissionsCard
 *  - Backend:  emissions calculation (factors.ts imports from here)
 */

export interface RefrigerantType {
    id: string;
    label: string;
    factor_kgCO2e_per_unit: number; // kgCO2e per kg leaked
}

export const REFRIGERANT_TYPES: RefrigerantType[] = [
    { id: "hfc_23", label: "HFC-23", factor_kgCO2e_per_unit: 12400 },
    { id: "hfc_32", label: "HFC-32", factor_kgCO2e_per_unit: 677 },
    { id: "hfc_41", label: "HFC-41", factor_kgCO2e_per_unit: 116 },
    { id: "hfc_125", label: "HFC-125", factor_kgCO2e_per_unit: 3170 },
    { id: "hfc_134", label: "HFC-134", factor_kgCO2e_per_unit: 1120 },
    { id: "hfc_134a", label: "HFC-134a", factor_kgCO2e_per_unit: 1300 },
    { id: "hfc_143", label: "HFC-143", factor_kgCO2e_per_unit: 328 },
    { id: "hfc_143a", label: "HFC-143a", factor_kgCO2e_per_unit: 4800 },
    { id: "hfc_152", label: "HFC-152", factor_kgCO2e_per_unit: 16 },
    { id: "hfc_152a", label: "HFC-152a", factor_kgCO2e_per_unit: 138 },
    { id: "hfc_161", label: "HFC-161", factor_kgCO2e_per_unit: 4 },
    { id: "hfc_227ea", label: "HFC-227ea", factor_kgCO2e_per_unit: 3350 },
    { id: "hfc_236cb", label: "HFC-236cb", factor_kgCO2e_per_unit: 1210 },
    { id: "hfc_236ea", label: "HFC-236ea", factor_kgCO2e_per_unit: 1330 },
    { id: "hfc_236fa", label: "HFC-236fa", factor_kgCO2e_per_unit: 8060 },
    { id: "hfc_245ca", label: "HFC-245ca", factor_kgCO2e_per_unit: 716 },
    { id: "hfc_245fa", label: "HFC-245fa", factor_kgCO2e_per_unit: 858 },
    { id: "hfc_365mfc", label: "HFC-365mfc", factor_kgCO2e_per_unit: 804 },
    { id: "hfc_43_10mee", label: "HFC-43-10mee", factor_kgCO2e_per_unit: 1650 },
    { id: "pfc_14", label: "Perfluoromethane (PFC-14)", factor_kgCO2e_per_unit: 6630 },
    { id: "pfc_116", label: "Perfluoroethane (PFC-116)", factor_kgCO2e_per_unit: 11100 },
    { id: "pfc_218", label: "Perfluoropropane (PFC-218)", factor_kgCO2e_per_unit: 8900 },
    { id: "pfc_318", label: "Perfluorocyclobutane (PFC-318)", factor_kgCO2e_per_unit: 9540 },
    { id: "pfc_3_1_10", label: "Perfluorobutane (PFC-3-1-10)", factor_kgCO2e_per_unit: 9200 },
    { id: "pfc_4_1_12", label: "Perfluoropentane (PFC-4-1-12)", factor_kgCO2e_per_unit: 8550 },
    { id: "pfc_5_1_14", label: "Perfluorohexane (PFC-5-1-14)", factor_kgCO2e_per_unit: 7910 },
    { id: "pfc_9_1_18", label: "PFC-9-1-18", factor_kgCO2e_per_unit: 7190 },
    { id: "perfluorocyclopropane", label: "Perfluorocyclopropane", factor_kgCO2e_per_unit: 9200 },
    { id: "sf6", label: "Sulphur hexafluoride (SF6)", factor_kgCO2e_per_unit: 23500 },
    { id: "nitrogen_trifluoride", label: "Nitrogen trifluoride", factor_kgCO2e_per_unit: 16100 },
    { id: "r401a", label: "R401A", factor_kgCO2e_per_unit: 1130 },
    { id: "r401b", label: "R401B", factor_kgCO2e_per_unit: 1236 },
    { id: "r401c", label: "R401C", factor_kgCO2e_per_unit: 876 },
    { id: "r402a", label: "R402A", factor_kgCO2e_per_unit: 2571 },
    { id: "r402b", label: "R402B", factor_kgCO2e_per_unit: 2261 },
    { id: "r403a", label: "R403A", factor_kgCO2e_per_unit: 3100 },
    { id: "r403b", label: "R403B", factor_kgCO2e_per_unit: 4457 },
    { id: "r404a", label: "R404A", factor_kgCO2e_per_unit: 3943 },
    { id: "r405a", label: "R405A", factor_kgCO2e_per_unit: 4821 },
    { id: "r406a", label: "R406A", factor_kgCO2e_per_unit: 1780 },
    { id: "r407a", label: "R407A", factor_kgCO2e_per_unit: 1923 },
    { id: "r407b", label: "R407B", factor_kgCO2e_per_unit: 2547 },
    { id: "r407c", label: "R407C", factor_kgCO2e_per_unit: 1624 },
    { id: "r407d", label: "R407D", factor_kgCO2e_per_unit: 1487 },
    { id: "r407e", label: "R407E", factor_kgCO2e_per_unit: 1425 },
    { id: "r407f", label: "R407F", factor_kgCO2e_per_unit: 1674 },
    { id: "r408a", label: "R408A", factor_kgCO2e_per_unit: 3257 },
    { id: "r409a", label: "R409A", factor_kgCO2e_per_unit: 1485 },
    { id: "r409b", label: "R409B", factor_kgCO2e_per_unit: 1474 },
    { id: "r410a", label: "R410A", factor_kgCO2e_per_unit: 1924 },
    { id: "r410b", label: "R410B", factor_kgCO2e_per_unit: 2048 },
    { id: "r411a", label: "R411A", factor_kgCO2e_per_unit: 1555 },
    { id: "r411b", label: "R411B", factor_kgCO2e_per_unit: 1659 },
    { id: "r412a", label: "R412A", factor_kgCO2e_per_unit: 2172 },
    { id: "r413a", label: "R413A", factor_kgCO2e_per_unit: 1945 },
    { id: "r414a", label: "R414A", factor_kgCO2e_per_unit: 1375 },
    { id: "r414b", label: "R414B", factor_kgCO2e_per_unit: 1274 },
    { id: "r415a", label: "R415A", factor_kgCO2e_per_unit: 1468 },
    { id: "r415b", label: "R415B", factor_kgCO2e_per_unit: 544 },
    { id: "r416a", label: "R416A", factor_kgCO2e_per_unit: 975 },
    { id: "r417a", label: "R417A", factor_kgCO2e_per_unit: 2127 },
    { id: "r417b", label: "R417B", factor_kgCO2e_per_unit: 2742 },
    { id: "r417c", label: "R417C", factor_kgCO2e_per_unit: 1643 },
    { id: "r418a", label: "R418A", factor_kgCO2e_per_unit: 1693 },
    { id: "r419a", label: "R419A", factor_kgCO2e_per_unit: 2688 },
    { id: "r419b", label: "R419B", factor_kgCO2e_per_unit: 2161 },
    { id: "r420a", label: "R420A", factor_kgCO2e_per_unit: 1382 },
    { id: "r421a", label: "R421A", factor_kgCO2e_per_unit: 2385 },
    { id: "r421b", label: "R421B", factor_kgCO2e_per_unit: 2890 },
    { id: "r422a", label: "R422A", factor_kgCO2e_per_unit: 2847 },
    { id: "r422b", label: "R422B", factor_kgCO2e_per_unit: 2290 },
    { id: "r422c", label: "R422C", factor_kgCO2e_per_unit: 2794 },
    { id: "r422d", label: "R422D", factor_kgCO2e_per_unit: 2473 },
    { id: "r422e", label: "R422E", factor_kgCO2e_per_unit: 2350 },
    { id: "r423a", label: "R423A", factor_kgCO2e_per_unit: 2274 },
    { id: "r424a", label: "R424A", factor_kgCO2e_per_unit: 2212 },
    { id: "r425a", label: "R425A", factor_kgCO2e_per_unit: 1431 },
    { id: "r426a", label: "R426A", factor_kgCO2e_per_unit: 1371 },
    { id: "r427a", label: "R427A", factor_kgCO2e_per_unit: 2024 },
    { id: "r428a", label: "R428A", factor_kgCO2e_per_unit: 3417 },
    { id: "r429a", label: "R429A", factor_kgCO2e_per_unit: 15.3 },
    { id: "r430a", label: "R430A", factor_kgCO2e_per_unit: 106 },
    { id: "r431a", label: "R431A", factor_kgCO2e_per_unit: 40 },
    { id: "r432a", label: "R432A", factor_kgCO2e_per_unit: 1.8 },
    { id: "r433a", label: "R433A", factor_kgCO2e_per_unit: 0.64 },
    { id: "r433b", label: "R433B", factor_kgCO2e_per_unit: 0.16 },
    { id: "r433c", label: "R433C", factor_kgCO2e_per_unit: 0.55 },
    { id: "r434a", label: "R434A", factor_kgCO2e_per_unit: 3076 },
    { id: "r435a", label: "R435A", factor_kgCO2e_per_unit: 28.4 },
    { id: "r436a", label: "R436A", factor_kgCO2e_per_unit: 1.35 },
    { id: "r436b", label: "R436B", factor_kgCO2e_per_unit: 1.47 },
    { id: "r437a", label: "R437A", factor_kgCO2e_per_unit: 1639 },
    { id: "r438a", label: "R438A", factor_kgCO2e_per_unit: 2059 },
    { id: "r439a", label: "R439A", factor_kgCO2e_per_unit: 1828 },
    { id: "r440a", label: "R440A", factor_kgCO2e_per_unit: 156 },
    { id: "r441a", label: "R441A", factor_kgCO2e_per_unit: 0.23 },
    { id: "r442a", label: "R442A", factor_kgCO2e_per_unit: 1754 },
    { id: "r443a", label: "R443A", factor_kgCO2e_per_unit: 1 },
    { id: "r444a", label: "R444A", factor_kgCO2e_per_unit: 89 },
    { id: "r445a", label: "R445A", factor_kgCO2e_per_unit: 118 },
    { id: "r500", label: "R500", factor_kgCO2e_per_unit: 7564 },
    { id: "r501", label: "R501", factor_kgCO2e_per_unit: 3870 },
    { id: "r502", label: "R502", factor_kgCO2e_per_unit: 4786 },
    { id: "r503", label: "R503", factor_kgCO2e_per_unit: 13299 },
    { id: "r504", label: "R504", factor_kgCO2e_per_unit: 4299 },
    { id: "r505", label: "R505", factor_kgCO2e_per_unit: 7956 },
    { id: "r506", label: "R506", factor_kgCO2e_per_unit: 3857 },
    { id: "r507a", label: "R507A", factor_kgCO2e_per_unit: 3985 },
    { id: "r508a", label: "R508A", factor_kgCO2e_per_unit: 11607 },
    { id: "r508b", label: "R508B", factor_kgCO2e_per_unit: 11698 },
    { id: "r509a", label: "R509A", factor_kgCO2e_per_unit: 5758 },
    { id: "r510a", label: "R510A", factor_kgCO2e_per_unit: 1.24 },
    { id: "r511a", label: "R511A", factor_kgCO2e_per_unit: 7 },
    { id: "r512a", label: "R512A", factor_kgCO2e_per_unit: 196 },
];

export function getRefrigerantTypeById(id: string): RefrigerantType | undefined {
    return REFRIGERANT_TYPES.find(r => r.id === id);
}
